import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users, Globe, MessageSquare, Plus, Edit, Trash2, FileText, Copy, Check, CheckCircle, X } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosCreds = { withCredentials: true };

const STATUSES = ["new", "contacted", "converted", "lost"];
const STATUS_COLORS = {
  new: { bg: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  contacted: { bg: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  converted: { bg: "bg-green-500/10 text-green-400 border-green-500/20" },
  lost: { bg: "bg-red-500/10 text-red-400 border-red-500/20" }
};

const ARCHETYPES = ["Explorer", "Romantic", "Celebrator", "Seeker", "Sensorialist", "Soul Traveler"];

// ── Standalone InputField to avoid focus-loss on re-render ───────────────────
const AdminInputField = ({ label, value, onChange, placeholder, required, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">
      {label} {required && <span className="text-[#C9A84C]">*</span>}
    </label>
    <input
      data-testid={testId}
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#C9A84C] outline-none text-white px-3 py-2 rounded-lg text-sm"
    />
  </div>
);
// ── DNA utility — module-scope so it is stable across renders ────────────────
const ARCHETYPE_COLORS = {
  Explorer: "#E8A838", Romantic: "#E8738A", Celebrator: "#A855F7",
  Seeker: "#22C55E", Sensorialist: "#F97316", "Soul Traveler": "#06B6D4"
};

function getDNASummary(scores) {
  if (!scores || !Object.keys(scores).length) return null;
  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  if (!total) return null;
  return Object.entries(scores)
    .map(([arch, score]) => ({ arch, pct: Math.round((score / total) * 100) }))
    .filter(x => x.pct > 0)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 3)
    .map(x => `${x.arch} ${x.pct}%`)
    .join(" · ");
}

function getDNABars(scores) {
  if (!scores || !Object.keys(scores).length) return [];
  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  if (!total) return [];
  return Object.entries(scores)
    .map(([arch, score]) => ({ arch, pct: Math.round((score / total) * 100), color: ARCHETYPE_COLORS[arch] || "#C9A84C" }))
    .filter(x => x.pct > 0)
    .sort((a, b) => b.pct - a.pct);
}

// ── DMC Brief Generator ───────────────────────────────────────────────────────
// PRIORITY 1 FIX: InputField wrapper was defined inside this function, causing
// React to unmount/remount the input on every keystroke (new function type each render).
// Fix: All text inputs now call AdminInputField directly with stable module-scope reference.
function DMCBriefGenerator() {
  const [form, setForm] = useState({
    destination: "", dates: "", duration: "", travelers: "2",
    archetype: "Explorer", secondary_archetype: "",
    accommodation: "Luxury", budget: "Luxury",
    travel_style: "", occasion: "", special_requirements: ""
  });
  const [brief, setBrief] = useState("");
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);

  useEffect(() => {
    axios.get(`${API}/leads`, axiosCreds)
      .then(res => setLeads(res.data))
      // Intentional: dashboard degrades gracefully if leads fail to load
      .catch(() => {});
  // API, axios, axiosCreds are stable module-level constants — empty deps correct
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const filteredLeads = leads.filter(l =>
    l.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.phone?.includes(searchTerm)
  ).slice(0, 8);

  // PRIORITY 4: Auto-populate form from selected lead — never overwrites manual entry
  const selectTraveler = (lead) => {
    const isSelected = selectedLeads.find(l => l._id === lead._id);
    const newSelected = isSelected
      ? selectedLeads.filter(l => l._id !== lead._id)
      : [...selectedLeads, lead];
    setSelectedLeads(newSelected);

    if (newSelected.length > 0) {
      const primary = newSelected[0];
      const archetypes = newSelected.map(l => l.archetype).filter(Boolean);
      const updates = {
        archetype: archetypes[0] || "Explorer",
        travelers: String(newSelected.length),
      };
      if (primary.secondary_archetype) updates.secondary_archetype = primary.secondary_archetype;
      // Only auto-fill travel_style if not already typed
      setForm(f => ({
        ...f,
        ...updates,
        travel_style: f.travel_style || primary.travel_type || primary.travel_companion || f.travel_style
      }));
    }
  };

  const generate = async () => {
    if (!form.destination || !form.departure_period || !form.duration) {
      setError("Destination, departure period and duration are required.");
      return;
    }
    setGenerating(true);
    setError("");
    setBrief("");
    try {
      const res = await axios.post(`${API}/admin/dmc-brief`, form, axiosCreds);
      setBrief(res.data.brief);
    } catch {
      setError("Failed to generate brief. Please try again.");
    } finally { setGenerating(false); }
  };

  const copyBrief = () => {
    navigator.clipboard?.writeText(brief).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      const el = document.querySelector('[data-testid="dmc-brief-text"]');
      if (el) { const range = document.createRange(); range.selectNode(el); window.getSelection()?.removeAllRanges(); window.getSelection()?.addRange(range); }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const primaryLead = selectedLeads[0];
  const dnaBars = primaryLead?.scores ? getDNABars(primaryLead.scores) : [];

  return (
    <div data-testid="dmc-brief-tab" className="max-w-4xl">
      <div className="mb-6">
        <p className="overline mb-2">AI-Powered</p>
        <h3 className="font-heading text-2xl font-light text-white mb-1">Generate DMC Brief</h3>
        <p className="text-[#71717A] text-sm">Select travelers from leads or enter details manually.</p>
      </div>

      {/* PRIORITY 3: Enhanced Traveler Selection Panel */}
      <div className="p-5 bg-[#111111] border border-[#C9A84C]/15 rounded-xl mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-3">Select Travelers from Leads</p>
        {/* Search input — AdminInputField at module scope → no focus loss */}
        <AdminInputField
          label=""
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name, email, or phone..."
          testId="dmc-lead-search"
        />
        <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
          {filteredLeads.map(lead => {
            const isSelected = selectedLeads.find(l => l._id === lead._id);
            const dnaSummary = getDNASummary(lead.scores);
            return (
              <button
                key={lead._id}
                onClick={() => selectTraveler(lead)}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  isSelected ? "border-[#C9A84C]/40 bg-[#C9A84C]/8" : "border-white/5 bg-[#0A0A0A] hover:border-white/15"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{lead.name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {lead.archetype && (
                        <span className="text-[10px] font-medium" style={{ color: ARCHETYPE_COLORS[lead.archetype] || "#C9A84C" }}>
                          {lead.archetype}
                        </span>
                      )}
                      {lead.secondary_archetype && (
                        <span className="text-[10px] text-[#71717A]">
                          ↳ {lead.secondary_archetype}
                        </span>
                      )}
                      {!lead.archetype && (
                        <span className="text-[10px] text-[#71717A]">{lead.email || lead.phone}</span>
                      )}
                    </div>
                    {dnaSummary && (
                      <p className="text-[9px] text-[#4A4A5A] mt-1 tracking-wide">{dnaSummary}</p>
                    )}
                  </div>
                  {isSelected && <span className="text-[#C9A84C] text-xs shrink-0 mt-0.5">✓</span>}
                </div>
              </button>
            );
          })}
          {filteredLeads.length === 0 && searchTerm && (
            <p className="text-xs text-[#71717A] text-center py-3">No travelers found</p>
          )}
        </div>

        {/* Selected traveler summary with full DNA */}
        {selectedLeads.length > 0 && (
          <div className="mt-4 p-4 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-[#C9A84C] font-medium">
                {selectedLeads.length} traveler{selectedLeads.length > 1 ? "s" : ""} selected
              </p>
              <button onClick={() => { setSelectedLeads([]); }} className="text-[9px] text-[#71717A] hover:text-[#C9A84C] uppercase tracking-wider">Clear</button>
            </div>
            {primaryLead && (
              <div>
                <div className="flex gap-3 mb-2">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Primary</p>
                    <p className="text-xs font-medium" style={{ color: ARCHETYPE_COLORS[form.archetype] || "#C9A84C" }}>{form.archetype}</p>
                  </div>
                  {form.secondary_archetype && (
                    <div>
                      <p className="text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-0.5">Secondary</p>
                      <p className="text-xs" style={{ color: ARCHETYPE_COLORS[form.secondary_archetype] || "#A1A1AA" }}>{form.secondary_archetype}</p>
                    </div>
                  )}
                </div>
                {dnaBars.length > 0 && (
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Travel DNA</p>
                    <div className="space-y-1.5">
                      {dnaBars.map(({ arch, pct, color }) => (
                        <div key={arch} className="flex items-center gap-2">
                          <span className="text-[9px] w-20 shrink-0" style={{ color }}>{arch}</span>
                          <div className="flex-1 h-0.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <span className="text-[9px] text-[#71717A] w-8 text-right">{pct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Trip Details + Traveler Profile */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="p-6 bg-[#111111] border border-white/5 rounded-xl space-y-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A84C]">Trip Details</p>
          {/* PRIORITY 1 FIX: Direct AdminInputField calls — no wrapper function */}
          <AdminInputField label="Destination" value={form.destination} onChange={e => set("destination", e.target.value)} placeholder="e.g. Patagonia, Kyoto, Maldives" required testId="dmc-destination" />
          <AdminInputField label="Preferred Departure Period" value={form.departure_period} onChange={e => set("departure_period", e.target.value)} placeholder="e.g. September 2026, Christmas Week, Flexible in August" required testId="dmc-dates" />
          <AdminInputField label="Duration" value={form.duration} onChange={e => set("duration", e.target.value)} placeholder="e.g. 7 nights" required testId="dmc-duration" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Number of Travelers</label>
            <select data-testid="dmc-travelers" value={form.travelers} onChange={e => set("travelers", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 text-white px-3 py-2 rounded-lg text-sm outline-none focus:border-[#C9A84C]">
              {["1","2","3","4","5","6","7","8","9","10+"].map(v => <option key={v} value={v}>{v} {v === "1" ? "person" : "people"}</option>)}
            </select>
          </div>
        </div>

        <div className="p-6 bg-[#111111] border border-white/5 rounded-xl space-y-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C9A84C]">Traveler Profile</p>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">
              Soulprint Archetype {selectedLeads.length > 0 && <span className="text-[#C9A84C] normal-case ml-1">(auto-filled)</span>}
            </label>
            {selectedLeads.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {ARCHETYPES.map(a => (
                  <span key={a} className={`text-xs px-3 py-1.5 rounded-sm border ${form.archetype === a ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]" : "border-white/5 text-[#71717A]"}`}>
                    {a}{form.archetype === a ? " ✓" : ""}
                  </span>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {ARCHETYPES.map(a => (
                  <button key={a} type="button" onClick={() => set("archetype", a)}
                    className={`text-xs px-3 py-1.5 rounded-sm border transition-all ${form.archetype === a ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]" : "border-white/10 text-[#A1A1AA] hover:border-white/25"}`}>
                    {a}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Accommodation</label>
            <div className="flex gap-2">
              {["Boutique", "Luxury", "Ultra-Luxury"].map(a => (
                <button key={a} type="button" onClick={() => set("accommodation", a)}
                  className={`text-xs px-3 py-1.5 rounded-sm border flex-1 ${form.accommodation === a ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]" : "border-white/10 text-[#A1A1AA]"}`}>
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Budget Category</label>
            <div className="flex gap-2">
              {["Premium", "Luxury", "Ultra"].map(b => (
                <button key={b} type="button" onClick={() => set("budget", b)}
                  className={`text-xs px-3 py-1.5 rounded-sm border flex-1 ${form.budget === b ? "border-[#C9A84C] bg-[#C9A84C]/10 text-[#C9A84C]" : "border-white/10 text-[#A1A1AA]"}`}>
                  {b}
                </button>
              ))}
            </div>
          </div>
          <AdminInputField label="Travel Style" value={form.travel_style} onChange={e => set("travel_style", e.target.value)} placeholder="e.g. Adventure-focused, cultural deep dive" testId="dmc-travel_style" />
        </div>
      </div>

      {/* Special Notes */}
      <div className="p-5 bg-[#111111] border border-white/5 rounded-xl mb-5">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C9A84C] mb-4">Special Notes</p>
        <div className="grid md:grid-cols-2 gap-4">
          <AdminInputField label="Occasion" value={form.occasion} onChange={e => set("occasion", e.target.value)} placeholder="e.g. Anniversary, Birthday, Solo escape" testId="dmc-occasion" />
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5">Custom Requirements</label>
            <textarea data-testid="dmc-special_requirements" value={form.special_requirements} onChange={e => set("special_requirements", e.target.value)} rows={2} placeholder="Dietary needs, accessibility, special requests..." className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#C9A84C] outline-none text-white px-3 py-2 rounded-lg text-sm resize-none" />
          </div>
        </div>
      </div>

      {error && <p className="text-red-400 text-xs mb-4">{error}</p>}

      <button data-testid="dmc-generate-btn" onClick={generate} disabled={generating} className="btn-gold text-xs px-10 py-3 flex items-center gap-2 disabled:opacity-40 mb-8">
        <FileText size={14} />
        {generating ? "Generating Brief..." : "Generate DMC Brief"}
      </button>

      {brief && (
        <div data-testid="dmc-brief-output" className="bg-[#111111] border border-[#C9A84C]/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A84C]">Generated Brief</p>
            <button onClick={copyBrief} className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#C9A84C] transition-colors">
              {copied ? <><Check size={12} className="text-[#C9A84C]" /> Copied</> : <><Copy size={12} /> Copy Brief</>}
            </button>
          </div>
          <pre data-testid="dmc-brief-text" className="text-sm text-[#D4D4D8] leading-relaxed whitespace-pre-wrap font-body">{brief}</pre>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [filterArchetype, setFilterArchetype] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [expModal, setExpModal] = useState(null); // null | 'add' | experience object
  const [expForm, setExpForm] = useState({});
  const [savingExp, setSavingExp] = useState(false);

  // API, axios, axiosCreds are stable module-level constants — filterArchetype/filterStatus are the only reactive deps
  const fetchData = useCallback(async () => {
    try {
      const [statsRes, leadsRes, expRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, axiosCreds),
        axios.get(`${API}/leads`, { ...axiosCreds, params: { archetype: filterArchetype || undefined, status: filterStatus || undefined } }),
        axios.get(`${API}/experiences`, axiosCreds)
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setExperiences(expRes.data);
    } catch (err) {
      console.error("[Admin] Dashboard data fetch failed:", err);
    } finally { setLoading(false); }
  // filterArchetype and filterStatus are the only reactive deps here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterArchetype, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const updateLeadStatus = async (id, status) => {
    await axios.put(`${API}/leads/${id}/status`, { status }, axiosCreds);
    setLeads(prev => prev.map(l => l._id === id ? { ...l, status } : l));
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/leads/${id}`, axiosCreds);
    setLeads(prev => prev.filter(l => l._id !== id));
    setStats(prev => prev ? { ...prev, total_leads: prev.total_leads - 1 } : prev);
  };

  const openExpModal = (exp = null) => {
    setExpModal(exp || "add");
    setExpForm(exp ? { ...exp } : {
      title: "", description: "", location: "", price_range: "",
      category: "Adventure", archetype: "Explorer", image_url: "",
      highlights: [], duration: "", is_featured: false
    });
  };

  const saveExp = async () => {
    setSavingExp(true);
    try {
      const body = { ...expForm, highlights: Array.isArray(expForm.highlights) ? expForm.highlights : (expForm.highlights || "").split(",").map(h => h.trim()).filter(Boolean) };
      if (expModal === "add") {
        const res = await axios.post(`${API}/experiences`, body, axiosCreds);
        setExperiences(prev => [...prev, res.data]);
      } else {
        await axios.put(`${API}/experiences/${expModal._id}`, body, axiosCreds);
        setExperiences(prev => prev.map(e => e._id === expModal._id ? { ...e, ...body } : e));
      }
      setExpModal(null);
    } catch (err) {
      console.error("[Admin] Experience save failed:", err);
    } finally { setSavingExp(false); }
  };

  const deleteExp = async (id) => {
    if (!window.confirm("Delete this experience?")) return;
    await axios.delete(`${API}/experiences/${id}`, axiosCreds);
    setExperiences(prev => prev.filter(e => e._id !== id));
  };

  // PRIORITY 5: Actionable stat cards rendered as plain clickable divs (no nested component)
  const renderStatCard = (Icon, label, value, sub, onClick) => (
    <div
      key={label}
      onClick={onClick}
      className={`bg-[#111111] border border-white/5 rounded-xl p-6 transition-all duration-200 ${onClick ? "cursor-pointer hover:border-[#C9A84C]/20" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs uppercase tracking-[0.2em] text-[#71717A]">{label}</p>
        <Icon size={16} className="text-[#C9A84C]" />
      </div>
      <p className="font-heading text-4xl font-light text-white mb-1">{value ?? "—"}</p>
      {sub && <p className="text-xs text-[#71717A]">{sub}</p>}
      {onClick && <p className="text-[9px] uppercase tracking-[0.15em] text-[#C9A84C]/40 mt-2">click to view →</p>}
    </div>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#111111] border-b border-white/5 px-6 md:px-10 py-4 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <span className="font-heading text-xl font-light text-white tracking-widest">Travelorza</span>
          <span className="text-[#71717A] text-xs px-2 py-0.5 bg-white/5 rounded">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-xs text-[#71717A] hidden md:block">{user?.email}</p>
          <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-2 text-xs text-[#A1A1AA] hover:text-[#C9A84C] transition-colors uppercase tracking-wider">
            <LogOut size={14} />
            Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto">
        {/* Stats */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => <div key={`stat-skel-${i}`} className="h-28 bg-[#111111] rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {renderStatCard(Users, "Total Leads", stats?.total_leads, `${stats?.new_leads} new`, () => { setActiveTab("leads"); setFilterStatus(""); })}
            {renderStatCard(Globe, "Experiences", stats?.total_experiences, null, () => setActiveTab("experiences"))}
            {renderStatCard(MessageSquare, "Concierge Chats", stats?.total_chats, null, () => setActiveTab("leads"))}
            {renderStatCard(CheckCircle, "New Leads", stats?.new_leads, "Awaiting contact", () => { setActiveTab("leads"); setFilterStatus("new"); })}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-6 border-b border-white/5 mb-6">
          {["leads", "experiences", "dmc"].map(tab => (
            <button
              key={tab}
              data-testid={`admin-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-xs uppercase tracking-[0.2em] transition-colors border-b-2 capitalize ${
                activeTab === tab ? "text-[#C9A84C] border-[#C9A84C]" : "text-[#71717A] border-transparent hover:text-white"
              }`}
            >
              {tab === "dmc" ? "DMC Brief" : tab}
            </button>
          ))}
        </div>

        {/* Leads Tab */}
        {activeTab === "leads" && (
          <div data-testid="leads-tab">
            {/* Filters */}
            <div className="flex gap-3 mb-5 flex-wrap">
              <select
                data-testid="leads-filter-archetype"
                value={filterArchetype}
                onChange={e => setFilterArchetype(e.target.value)}
                className="bg-[#111111] border border-white/10 text-[#A1A1AA] text-xs px-3 py-2 rounded-lg outline-none focus:border-[#C9A84C]"
              >
                <option value="">All Archetypes</option>
                {["Explorer","Romantic","Celebrator","Seeker","Sensorialist","Soul Traveler"].map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              <select
                data-testid="leads-filter-status"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="bg-[#111111] border border-white/10 text-[#A1A1AA] text-xs px-3 py-2 rounded-lg outline-none focus:border-[#C9A84C]"
              >
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-xl border border-white/5">
              <table className="w-full" data-testid="leads-table">
                <thead>
                  <tr className="bg-[#111111] border-b border-white/5">
                    {["Name", "Phone", "Email", "Soulprint Intelligence", "Travelers", "Type", "Experience", "Companion", "Status", "Date", "Actions"].map(h => (
                      <th key={h} className="text-left text-[10px] uppercase tracking-[0.2em] text-[#71717A] px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead._id} data-testid={`lead-row-${lead._id}`} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                      <td className="px-4 py-3 text-sm text-white whitespace-nowrap">{lead.name}</td>
                      <td className="px-4 py-3 text-sm text-[#A1A1AA] whitespace-nowrap">{lead.phone}</td>
                      <td className="px-4 py-3 text-sm text-[#A1A1AA] whitespace-nowrap">{lead.email || "—"}</td>
                      <td className="px-4 py-3 text-xs" style={{ minWidth: 160 }}>
                        {lead.archetype ? (
                          <div>
                            <p className="font-medium" style={{ color: ARCHETYPE_COLORS[lead.archetype] || "#C9A84C" }}>
                              {lead.archetype}
                            </p>
                            {lead.secondary_archetype && (
                              <p className="text-[10px] mt-0.5" style={{ color: ARCHETYPE_COLORS[lead.secondary_archetype] || "#71717A", opacity: 0.8 }}>
                                ↳ {lead.secondary_archetype}
                              </p>
                            )}
                            {lead.scores && Object.keys(lead.scores).length > 0 && (() => {
                              const summary = getDNASummary(lead.scores);
                              return summary ? <p className="text-[9px] text-[#4A4A5A] mt-1 leading-relaxed">{summary}</p> : null;
                            })()}
                          </div>
                        ) : (
                          <span className="text-[#71717A]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{lead.number_of_travelers || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{lead.travel_type || lead.travel_companion || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA] max-w-[120px] truncate">{lead.preferred_experience || "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{lead.travel_companion || "—"}</td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`lead-status-${lead._id}`}
                          value={lead.status}
                          onChange={e => updateLeadStatus(lead._id, e.target.value)}
                          className={`text-[10px] px-2 py-1 rounded border outline-none ${STATUS_COLORS[lead.status]?.bg || "text-[#A1A1AA] border-white/10"} bg-transparent cursor-pointer`}
                        >
                          {STATUSES.map(s => <option key={s} value={s} className="bg-[#111111] text-white">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#71717A] whitespace-nowrap">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <button data-testid={`delete-lead-${lead._id}`} onClick={() => deleteLead(lead._id)} className="text-[#71717A] hover:text-red-400 transition-colors p-1">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-[#71717A] text-sm">No leads yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Experiences Tab */}
        {activeTab === "experiences" && (
          <div data-testid="experiences-tab">
            <div className="flex justify-end mb-5">
              <button
                data-testid="add-experience-btn"
                onClick={() => openExpModal()}
                className="btn-gold text-[10px] px-6 py-2.5 flex items-center gap-2"
              >
                <Plus size={14} />
                Add Experience
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-white/5">
              <table className="w-full" data-testid="experiences-table">
                <thead>
                  <tr className="bg-[#111111] border-b border-white/5">
                    {["Title", "Category", "Archetype", "Location", "Price", "Featured", "Actions"].map(h => (
                      <th key={h} className="text-left text-[10px] uppercase tracking-[0.2em] text-[#71717A] px-4 py-3 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {experiences.map(exp => (
                    <tr key={exp._id} data-testid={`exp-row-${exp._id}`} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                      <td className="px-4 py-3 text-sm text-white max-w-[200px] truncate">{exp.title}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{exp.category}</td>
                      <td className="px-4 py-3 text-xs text-[#C9A84C]">{exp.archetype}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{exp.location}</td>
                      <td className="px-4 py-3 text-xs text-[#A1A1AA]">{exp.price_range}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className={`px-2 py-0.5 rounded-sm border text-[10px] ${exp.is_featured ? "bg-[#C9A84C]/10 text-[#C9A84C] border-[#C9A84C]/20" : "text-[#71717A] border-white/10"}`}>
                          {exp.is_featured ? "Featured" : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 flex items-center gap-2">
                        <button data-testid={`edit-exp-${exp._id}`} onClick={() => openExpModal(exp)} className="text-[#71717A] hover:text-[#C9A84C] transition-colors p-1"><Edit size={14} /></button>
                        <button data-testid={`delete-exp-${exp._id}`} onClick={() => deleteExp(exp._id)} className="text-[#71717A] hover:text-red-400 transition-colors p-1"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* DMC Brief Tab */}
      {activeTab === "dmc" && (
        <DMCBriefGenerator />
      )}

      {/* Experience Form Modal */}
      {expModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.85)" }} onClick={e => { if (e.target === e.currentTarget) setExpModal(null); }}>
          <div data-testid="exp-form-modal" className="bg-[#111111] border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-2xl font-light text-white">{expModal === "add" ? "Add Experience" : "Edit Experience"}</h3>
              <button onClick={() => setExpModal(null)} className="text-[#71717A] hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {[
                { key: "title", label: "Title", type: "text" },
                { key: "location", label: "Location", type: "text" },
                { key: "price_range", label: "Price Range", type: "text", placeholder: "$1000-$2000" },
                { key: "duration", label: "Duration", type: "text", placeholder: "3 nights" },
                { key: "image_url", label: "Image URL", type: "text" }
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">{f.label}</label>
                  <input
                    data-testid={`exp-form-${f.key}`}
                    type={f.type}
                    value={expForm[f.key] || ""}
                    onChange={e => setExpForm({ ...expForm, [f.key]: e.target.value })}
                    placeholder={f.placeholder || ""}
                    className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#C9A84C] outline-none text-white px-3 py-2 rounded-lg text-sm"
                  />
                </div>
              ))}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Description</label>
                <textarea
                  data-testid="exp-form-description"
                  value={expForm.description || ""}
                  onChange={e => setExpForm({ ...expForm, description: e.target.value })}
                  rows={3}
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#C9A84C] outline-none text-white px-3 py-2 rounded-lg text-sm resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Category</label>
                  <select
                    data-testid="exp-form-category"
                    value={expForm.category || "Adventure"}
                    onChange={e => setExpForm({ ...expForm, category: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg outline-none focus:border-[#C9A84C]"
                  >
                    {["Adventure","Romance","Luxury","Spiritual","Music Tourism","Nature Escapes"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Archetype</label>
                  <select
                    data-testid="exp-form-archetype"
                    value={expForm.archetype || "Explorer"}
                    onChange={e => setExpForm({ ...expForm, archetype: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-white/10 text-white text-sm px-3 py-2 rounded-lg outline-none focus:border-[#C9A84C]"
                  >
                    {["Explorer","Romantic","Celebrator","Seeker","Sensorialist","Soul Traveler"].map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  data-testid="exp-form-featured"
                  type="checkbox"
                  id="featured"
                  checked={!!expForm.is_featured}
                  onChange={e => setExpForm({ ...expForm, is_featured: e.target.checked })}
                  className="w-4 h-4 accent-[#C9A84C]"
                />
                <label htmlFor="featured" className="text-xs text-[#A1A1AA] uppercase tracking-[0.15em]">Featured on Homepage</label>
              </div>
            </div>
            <button
              data-testid="exp-form-save-btn"
              onClick={saveExp}
              disabled={savingExp}
              className="w-full btn-gold mt-6 py-3 text-xs disabled:opacity-50"
            >
              {savingExp ? "Saving..." : "Save Experience"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
